// src/constants/seo-event-enhanced.ts
// ============================================================================
// SEO Event-Aware Enhanced Builders (Title/Desc + JSON-LD Graph)
// - Copy is SSOT-based: uses core.ts WORDS + makeMetaDescriptionForType
// - No "בת-מצווה" key: בר/בת מצווה is always combined (per WORDS)
// ============================================================================

import {
    SITE_BASE,
    BUSINESS_ID_ABS,
    OG_PRIMARY_IMAGE,
    POSTER_URL,
    withBet,
    makeMetaDescriptionForType,
    type SeoService,
    serviceByType,
    SEO_SERVICE_WEDDING,
    SEO_SERVICE_HENNA,
    SEO_SERVICE_BAR_BAT,
    SEO_SERVICE_EVENT,
    type BusinessOffice,
  } from './core';

  import {
    preferredKeywordForService,
    BRAND_TITLE_HE,
    BRAND_TITLE_SHORT,
    WORDS,
    HTML_LANG_FULL,
    BUSINESS,
  } from '../i18n/locales/i18n.he';
  
  import type { EventType } from './geo';
  
  type AnyObj = Record<string, any>;
  
  /* ─────────────────────────────────────────────────────────────
     Helpers
     ───────────────────────────────────────────────────────────── */


    function absUrlLocal(pathOrAbs: string) {
    try {
      const already = new URL(pathOrAbs);
      return already.toString();
    } catch {}
  
    const p = pathOrAbs.startsWith('/') ? pathOrAbs : `/${pathOrAbs}`;
    const base = new URL(SITE_BASE);
  
    const decoded = (() => { try { return decodeURI(p); } catch { return p; } })();
    base.pathname = decoded
      .split('/')
      .map((seg, i) => (i === 0 ? '' : encodeURIComponent(seg)))
      .join('/');
  
    return base.toString();
  }

  const isStr = (x: any): x is string => typeof x === 'string' && x.trim().length > 0;
  const clean = (s: any) => String(s ?? '').replace(/\s+/g, ' ').trim();
  const ensureTrailingSlash = (u: string) => (u.endsWith('/') ? u : `${u}/`);
  const uniq = (arr: string[]) => Array.from(new Set(arr.map((x) => clean(x)).filter(Boolean)));
  const safeBaseNoHash = (u: string) => ensureTrailingSlash(String(u).split('#')[0]);
  
  export function mapInternalLinks(
    links?: Array<{ label: string; href: string }>
  ): Array<{ label: string; href: string }> {
    if (!Array.isArray(links)) return [];
    return links
      .filter((l) => l && isStr(l.label) && isStr(l.href))
      .map((l) => ({
        label: clean(l.label),
        href: ensureTrailingSlash(l.href.startsWith('http') ? l.href : absUrlLocal(l.href)),
      }));
  }
  
  /**
   * Normalize EventType string into SSOT categories
   * - בר-מצווה / בת-מצווה / "בר/בת מצווה" -> בר-מצווה (SSOT mapping uses that)
   * - anything else falls back to "אירוע"
   */
  function normalizeEventType(raw?: EventType | string): EventType {
    const t = clean(raw || '');
    if (t === 'חתונה') return 'חתונה';
    if (t === 'חינה') return 'חינה';
  
    // bar/bat variants (we still treat everything as bar-bat in copy)
    if (
      t === 'בר-מצווה' ||
      t === 'בת-מצווה' ||
      t === WORDS.events.bar_bat.one ||
      t.replace(/[״׳"']/g, '') === 'בר/בת מצווה'
    ) {
      return 'בר-מצווה';
    }
  
    return 'אירוע';
  }
  
  function serviceFromTypeOrService(typeOrService?: EventType | SeoService | string): SeoService {
    const normalized = normalizeEventType(typeOrService as any);
    if ((normalized as EventType) in serviceByType) return serviceByType[normalized as EventType];
    // If someone passed a SeoService string directly
    const maybe = clean(typeOrService);
    if (
      maybe === SEO_SERVICE_WEDDING ||
      maybe === SEO_SERVICE_HENNA ||
      maybe === SEO_SERVICE_BAR_BAT ||
      maybe === SEO_SERVICE_EVENT
    ) {
      return maybe as SeoService;
    }
    return SEO_SERVICE_EVENT;
  }
  
  /* ─────────────────────────────────────────────────────────────
     Event-aware Copy (SSOT-based)
     ───────────────────────────────────────────────────────────── */
  
  type EventCopy = {
    title: (loc?: string) => string;
    desc: (loc?: string) => string;
    knowsAbout: string[];
    offers: Array<{ name: string; desc: string }>;
    category: string;
  };
  
  function cityOrIsrael(loc?: string) {
    const L = clean(loc || '');
    return L && L !== 'ישראל' ? L : '';
  }
  
  function keywordForService(service: SeoService) {
    // Matches your SSOT preference: "דיג׳יי ל..." first if available
    return preferredKeywordForService(service);
  }
  
  function baseTitle(service: SeoService, loc?: string) {
    const city = clean(loc || '');
    return `${keywordForService(service)}${withBet(city)} - ${BRAND_TITLE_HE}`;
  }
  
  function baseDesc(typeOrService: EventType | SeoService, loc?: string) {
    // Uses your SSOT meta description builder (15 yrs experience, etc.)
    return makeMetaDescriptionForType(typeOrService, clean(loc || ''));
  }
  
// ✅ Replace ONLY these parts in src/constants/seo-event-enhanced.ts
// Goal: טקסט “חד”, “אמיתי”, בסגנון הרשימה שלך.
// עיקרון: משפטים קצרים, מילות פעולה, בלי סיפורים/פואטיקה.
// ✅ "אני די ג׳יי + ל מילת סוג אירוע" נשמר בכל knowsAbout + offers.

function knowsAboutFor(service: SeoService): string[] {
    const base = uniq([
      'מיקסים מדויקים',
      'קריאת קהל חדה',
      'שילוב סגנונות',
      'בחירת שירים נכונה',
      'בניית קו מוזיקלי',
      'יצירת פסקול מותאם אישית',
      'ניהול אירוע נכון',
      'אנרגיה בעמדה',
      'רחבת ריקודים מלאה',
      'להיטים עדכניים ונוסטלגיים',
      'ניסיון 15 שנים',
    ]);
  
    if (service === SEO_SERVICE_WEDDING) {
      return uniq([
        `${WORDS.dj.he_alt} ${WORDS.events.wedding.to}`,
        ...base,
        'קבלת פנים',
        'חופה',
        'שיר כניסה לחופה',
        'שיר שבירת הכוס',
        'סלואו ראשון',
        'מעבר טבעי לחגיגה',
        'ניהול לוז חתונה',
      ]);
    }
  
    if (service === SEO_SERVICE_HENNA) {
      return uniq([
        `${WORDS.dj.he_alt} ${WORDS.events.henna.to}`,
        ...base,
        'טקס חינה',
        'מקצבים מזרחיים',
        'מעגלים',
        'שילוב מסורת ומודרני',
        'תיאום כניסות ורגעים',
      ]);
    }
  
    if (service === SEO_SERVICE_BAR_BAT) {
      return uniq([
        `${WORDS.dj.he_alt} ${WORDS.events.bar_bat.to}`,
        ...base,
        'סטים לנוער',
        'ניהול רחבה לפי גילאים',
        'להיטים לנוער',
        'קלאסיקות למשפחה',
        'כניסת מצווה',
      ]);
    }
  
    return uniq([
      `${WORDS.dj.he_alt} ${WORDS.events.generic.to}`,
      ...base,
      WORDS.phrases.company_or_private,
    ]);
  }
  
  function offersFor(service: SeoService): Array<{ name: string; desc: string }> {
    // פורמט: שם שירות קצר + תיאור קצר “מה בפועל קורה”
    if (service === SEO_SERVICE_WEDDING) {
      return [
        {
          name: `${WORDS.dj.he_alt} ${WORDS.events.wedding.to} – פסקול מלא לערב`,
          desc: 'בניית קו מוזיקלי, בחירת שירים נכונה, מיקסים מדויקים, קריאת קהל חדה, רחבה מלאה לאורך כל הערב.',
        },
        {
          name: 'חופה וסלואו – תיאום שירים ותזמון',
          desc: 'בחירת שירים לרגעים המרכזיים ותיאום תזמונים לכניסות, שבירת הכוס וריקוד ראשון.',
        },
        {
          name: 'ניהול אירוע – קצב, אנרגיה, רצף',
          desc: 'ניהול לוז מוזיקלי בזמן אמת, מעברים חדים ושילוב סגנונות כדי לשמור על אנרגיה גבוהה.',
        },
      ];
    }
  
    if (service === SEO_SERVICE_HENNA) {
      return [
        {
          name: `${WORDS.dj.he_alt} ${WORDS.events.henna.to} – מסורת + רחבה`,
          desc: 'שילוב מסורת ומודרני, מקצבים מזרחיים, בחירת שירים נכונה, מיקסים מדויקים וקריאת קהל חדה.',
        },
        {
          name: 'טקס חינה – רגעים מדויקים',
          desc: 'תיאום כניסות ורגעים מרכזיים, מוזיקה לטקס ולמעגלים, ואז מעבר חד לרחבה.',
        },
      ];
    }
  
    if (service === SEO_SERVICE_BAR_BAT) {
      return [
        {
          name: `${WORDS.dj.he_alt} ${WORDS.events.bar_bat.to} – להיטים לנוער + משפחה`,
          desc: 'להיטים עדכניים ונוסטלגיים, סטים לנוער, קלאסיקות למשפחה, שילוב סגנונות וקריאת קהל חדה.',
        },
        {
          name: 'כניסת מצווה ורחבה מלאה',
          desc: 'בחירת שיר כניסה, בניית שיאים לאורך הערב, אנרגיה בעמדה ומיקסים שמחזיקים את הרחבה.',
        },
      ];
    }
  
    return [
      {
        name: `${WORDS.dj.he_alt} ${WORDS.events.generic.to} – מותאם לקהל`,
        desc: 'יצירת פסקול מותאם אישית, בניית קו מוזיקלי, מיקסים מדויקים, קריאת קהל חדה וניהול אירוע נכון.',
      },
      {
        name: 'מוזיקה לרגעים מרכזיים',
        desc: 'בחירת שירים נכונה לברכות/טקסים/רגעי שיא, עם מעברים נקיים כדי לשמור על רצף.',
      },
    ];
  }

  export const EVENT_COPY_BY_SERVICE: Record<SeoService, EventCopy> = {
    [SEO_SERVICE_WEDDING]: {
      title: (loc) => baseTitle(SEO_SERVICE_WEDDING, loc),
      desc: (loc) => baseDesc('חתונה', loc),
      knowsAbout: knowsAboutFor(SEO_SERVICE_WEDDING),
      offers: offersFor(SEO_SERVICE_WEDDING),
      category: 'Wedding',
    },
  
    [SEO_SERVICE_HENNA]: {
      title: (loc) => baseTitle(SEO_SERVICE_HENNA, loc),
      desc: (loc) => baseDesc('חינה', loc),
      knowsAbout: knowsAboutFor(SEO_SERVICE_HENNA),
      offers: offersFor(SEO_SERVICE_HENNA),
      category: 'Event',
    },
  
    [SEO_SERVICE_BAR_BAT]: {
      title: (loc) => baseTitle(SEO_SERVICE_BAR_BAT, loc),
      desc: (loc) => baseDesc('בר-מצווה', loc),
      knowsAbout: knowsAboutFor(SEO_SERVICE_BAR_BAT),
      offers: offersFor(SEO_SERVICE_BAR_BAT),
      category: 'Event',
    },
  
    [SEO_SERVICE_EVENT]: {
      title: (loc) => baseTitle(SEO_SERVICE_EVENT, loc),
      desc: (loc) => baseDesc('אירוע', loc),
      knowsAbout: knowsAboutFor(SEO_SERVICE_EVENT),
      offers: offersFor(SEO_SERVICE_EVENT),
      category: 'Event',
    },
  };
  
  export function seoCopyByEvent(params: {
    eventType?: EventType | string;
    loc?: string;
    brand?: string; // kept for compatibility, but SSOT brand is BRAND_TITLE_HE
  }) {
    const loc = cityOrIsrael(params.loc);
    const service = serviceFromTypeOrService(params.eventType);
  
    const dict = EVENT_COPY_BY_SERVICE[service] ?? EVENT_COPY_BY_SERVICE[SEO_SERVICE_EVENT];
  
    return {
      title: dict.title(loc),
      desc: dict.desc(loc),
      knowsAbout: dict.knowsAbout,
      offers: dict.offers,
      category: dict.category,
    };
  }
  
  /* ─────────────────────────────────────────────────────────────
     JSON-LD Enhanced Builders
     ───────────────────────────────────────────────────────────── */
  
  export type EnhancedSeoOpts = {
    pageUrlAbsWithSlash: string;
    pageTitle: string;
    pageDesc: string;
  
    serviceLabel?: string;
    locationLabel?: string;
    eventType?: EventType | string;
  
    navLinks?: Array<{ label: string; href: string }>;
    businessOffice?: BusinessOffice;
    reviews?: Array<{
      rating: number;
      author?: string;
      text?: string;
      dateISO?: string;
      source?: 'google' | string;
      profileUrl?: string;
    }>;
    video?: {
      contentUrl: string;
      uploadDateISO: string;
      durationISO8601: string;
      clips?: Array<{ name: string; startSeconds: number; endSeconds: number; url?: string }>;
    };
  };
  
  function googlePlaceCanonical(office?: BusinessOffice): string | undefined {
    const rawId =
      office && Object.prototype.hasOwnProperty.call(office, 'googlePlaceId')
        ? office.googlePlaceId
        : (BUSINESS as any)?.googlePlaceId;

    const id = typeof rawId === 'string' ? rawId.trim() : '';
    if (isStr(id)) {
      return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(id)}`;
    }

    if (office) return undefined;

    const fromAddress = (() => {
      try {
        const q = encodeURIComponent(
          [BUSINESS?.address?.street, BUSINESS?.address?.city].filter(Boolean).join(', ')
        );
        return q ? `https://www.google.com/maps?q=${q}` : undefined;
      } catch {
        return undefined;
      }
    })();
    return BUSINESS?.address?.mapUrl || fromAddress || undefined;
  }
  
function buildPersonNode(knowsAboutExtra: string[], office?: BusinessOffice): AnyObj {
  const personId = `${SITE_BASE}#person`;
  const sameAs = uniq([...(office?.sameAs ?? BUSINESS?.sameAs ?? [])]);

  const base = [
    'מיקסים מדויקים',
    'קריאת קהל חדה',
    'שילוב סגנונות',
    'בחירת שירים נכונה',
    'בניית קו מוזיקלי',
    'יצירת פסקול מותאם אישית',
    'ניהול אירוע נכון',
    'אנרגיה בעמדה',
    'רחבת ריקודים מלאה',
    'להיטים עדכניים ונוסטלגיים',
    'ניסיון 15 שנים',
  ];

  return {
    '@type': 'Person',
    '@id': personId,
    name: (BUSINESS as any)?.founderName || BRAND_TITLE_SHORT,
    alternateName: ['DJ Elior Mor', BRAND_TITLE_SHORT].filter(Boolean),
    jobTitle: WORDS.dj.en,
    worksFor: { '@id': BUSINESS_ID_ABS || `${SITE_BASE}#business` },
    url: SITE_BASE,
    image: OG_PRIMARY_IMAGE,
    sameAs: sameAs.length ? sameAs : undefined,
    knowsAbout: uniq([...base, ...knowsAboutExtra]),
  };
}
  
  function buildOrganizationPatches(offerCatalogId: string, office?: BusinessOffice): AnyObj[] {
    const place = googlePlaceCanonical(office);
    const sameAs = uniq([
      ...(office?.sameAs ?? BUSINESS?.sameAs ?? []),
      ...(place ? [place] : []),
    ]);
  
    return [
      {
        '@id': BUSINESS_ID_ABS || `${SITE_BASE}#business`,
        founder: { '@id': `${SITE_BASE}#person` },
        sameAs: sameAs.length ? sameAs : undefined,
      },
      {
        '@id': BUSINESS_ID_ABS || `${SITE_BASE}#business`,
        hasOfferCatalog: { '@id': offerCatalogId },
      },
    ];
  }
  
  function buildOfferCatalog(
    opts: EnhancedSeoOpts,
    offers: Array<{ name: string; desc: string }>,
    category: string
  ): AnyObj {
    const location = clean(opts.locationLabel || '');
    const pageUrl = safeBaseNoHash(opts.pageUrlAbsWithSlash);
    const catalogId = `${pageUrl}#offer-catalog`;
  
    const items = offers.map((it, idx) => ({
      '@type': 'Offer',
      '@id': `${pageUrl}#offer-${idx + 1}`,
      name: it.name,
      category,
      description: it.desc,
      availability: 'https://schema.org/InStock',
      seller: { '@id': BUSINESS_ID_ABS || `${SITE_BASE}#business` },
      areaServed:
        location === 'ישראל'
          ? { '@type': 'Country', name: 'Israel' }
          : { '@type': 'City', name: location },
      itemOffered: {
        '@type': 'Service',
        name: it.name,
        serviceType: clean(opts.eventType || opts.serviceLabel || `${WORDS.dj.he_alt} ${WORDS.events.generic.to}`),
        provider: { '@id': BUSINESS_ID_ABS || `${SITE_BASE}#business` },
      },
    }));
  
    return {
      '@type': 'OfferCatalog',
      '@id': catalogId,
      name: `${BRAND_TITLE_HE} — קטלוג שירותים`,
      itemListElement: items.map((offer, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: offer,
      })),
    };
  }
  
  function buildNavigation(opts: EnhancedSeoOpts): AnyObj | null {
    const links = Array.isArray(opts.navLinks) ? opts.navLinks : [];
    if (!links.length) return null;
  
    return {
      '@type': 'SiteNavigationElement',
      '@id': `${opts.pageUrlAbsWithSlash}#sitenav`,
      name: 'ניווט באתר',
      inLanguage: HTML_LANG_FULL,
      hasPart: links.slice(0, 24).map((l, idx) => ({
        '@type': 'WebPage',
        '@id': `${opts.pageUrlAbsWithSlash}#nav-${idx + 1}`,
        name: clean(l.label),
        url: ensureTrailingSlash(l.href),
        isPartOf: { '@id': `${SITE_BASE}#website` },
      })),
    };
  }

  function buildVideoWithClips(opts: EnhancedSeoOpts): AnyObj[] {
    if (!opts.video || !isStr(opts.video.contentUrl)) return [];
  
    const pageUrl = safeBaseNoHash(opts.pageUrlAbsWithSlash);
    const videoId = `${pageUrl}#video`;
  
    const video: AnyObj = {
      '@type': 'VideoObject',
      '@id': videoId,
      name: clean(opts.pageTitle),
      description: clean(opts.pageDesc),
      thumbnailUrl: [POSTER_URL],
      uploadDate: opts.video.uploadDateISO,
      duration: opts.video.durationISO8601,
      contentUrl: opts.video.contentUrl,
      inLanguage: HTML_LANG_FULL,
      isFamilyFriendly: true,
      publisher: {
        '@type': 'Organization',
        name: BRAND_TITLE_HE,
        logo: { '@id': `${SITE_BASE}#logo` },
      },
      potentialAction: [{ '@type': 'WatchAction', target: opts.video.contentUrl }],
    };
  
    const clips = Array.isArray(opts.video.clips) ? opts.video.clips : [];
    if (!clips.length) return [video];
  
    const clipNodes = clips.slice(0, 12).map((c, idx) => {
      const start = Math.max(0, Math.floor(Number(c.startSeconds || 0)));
      const end = Math.max(start + 1, Math.floor(Number(c.endSeconds || start + 15)));
      return {
        '@type': 'Clip',
        '@id': `${videoId}#clip-${idx + 1}`,
        name: clean(c.name || `קטע ${idx + 1}`),
        startOffset: start,
        endOffset: end,
        url: c.url ? clean(c.url) : pageUrl,
        isPartOf: { '@id': videoId },
      };
    });
  
    (video as any).hasPart = clipNodes.map((x) => ({ '@id': x['@id'] }));

    return [
      video,
      ...clipNodes,
    ];
  }
  
  export function buildEnhancedSeoNodes(opts: EnhancedSeoOpts): object[] {
    const location = clean(opts.locationLabel || 'ישראל');
  
    const copy = seoCopyByEvent({
      eventType: opts.eventType,
      loc: location,
      brand: BRAND_TITLE_HE,
    });
  
    const person = buildPersonNode(copy.knowsAbout, opts.businessOffice);
    const offerCatalog = buildOfferCatalog(opts, copy.offers, copy.category);
    const orgPatches = buildOrganizationPatches(
      String((offerCatalog as AnyObj)['@id']),
      opts.businessOffice
    );
    const nav = buildNavigation(opts);
    const videoNodes = buildVideoWithClips(opts);
  
    return [
      person,
      ...orgPatches,
      offerCatalog,
      ...(nav ? [nav] : []),
      ...videoNodes,
    ].filter(Boolean) as object[];
  }
  
  /* ─────────────────────────────────────────────────────────────
     Convenience: Build Copy (Title/Desc) + Enhanced Nodes Together
     ───────────────────────────────────────────────────────────── */
  
  export function buildSeoEventBundle(opts: EnhancedSeoOpts) {
    const location = clean(opts.locationLabel || 'ישראל');
    const copy = seoCopyByEvent({
      eventType: opts.eventType,
      loc: location,
      brand: BRAND_TITLE_HE,
    });
  
    const enhancedNodes = buildEnhancedSeoNodes({
      ...opts,
      pageTitle: copy.title,
      pageDesc: copy.desc,
    });
  
    return {
      title: copy.title,
      description: copy.desc,
      knowsAbout: copy.knowsAbout,
      offers: copy.offers,
      enhancedNodes,
    };
  }
